import { getLanguageCode } from 'analytics/Analytics'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'
import { isGroupcalApp } from 'utils/appType'
import { isTestEnv } from 'utils/env'

// we do not want to use category 285 in production, leaving it only for test envs
const WHATS_NEW_CATEGORY_IDS = isTestEnv() ? '46,285' : '46'
const WHATS_NEW_FIELDS = 'id,content,featured_image_src'

interface FeaturedImageVariant {
  source_url?: string
  url?: string
}

type FeaturedImageSource =
  | string
  | FeaturedImageVariant
  | FeaturedImageVariant[]
  | null
  | undefined

export interface WordPressRenderedValue {
  rendered: string
}

export interface WhatsNewPost {
  id: number
  content: WordPressRenderedValue
  featured_image_src?: FeaturedImageSource
  featuredImageUrl?: string
}

function resolveFeaturedImageUrl(source: FeaturedImageSource): string | null {
  if (!source) return null

  if (typeof source === 'string') {
    return source.trim().length > 0 ? source : null
  }

  if (Array.isArray(source)) {
    for (const item of source) {
      const candidate = item?.source_url ?? item?.url
      if (candidate && candidate.trim().length > 0) return candidate
    }
    return null
  }

  const candidate = source.source_url ?? source.url
  return candidate && candidate.trim().length > 0 ? candidate : null
}

function sanitizeWordPressHtml(html: string) {
  if (!html || typeof DOMParser === 'undefined') return html

  const doc = new DOMParser().parseFromString(html, 'text/html')

  doc.querySelectorAll('script, style, noscript').forEach((node) => {
    node.remove()
  })

  doc.querySelectorAll<HTMLElement>('*').forEach((element) => {
    element.removeAttribute('width')
    element.removeAttribute('height')
    element.removeAttribute('srcset')
    element.removeAttribute('sizes')
    element.removeAttribute('loading')
    element.removeAttribute('decoding')
  })

  doc.querySelectorAll('a').forEach((link) => {
    link.setAttribute('target', '_blank')
    link.setAttribute('rel', 'noopener noreferrer')
  })

  // WordPress classic editor output often wraps each text run with spans.
  // Unwrapping these improves consistency with our own typography styles.
  doc.querySelectorAll('span').forEach((span) => {
    if (!span.attributes.length) {
      span.replaceWith(...Array.from(span.childNodes))
    }
  })

  return doc.body.innerHTML
}

function normalizePost(post: WhatsNewPost): WhatsNewPost {
  const featuredImageUrl = resolveFeaturedImageUrl(post.featured_image_src)

  return {
    ...post,
    content: {
      rendered: sanitizeWordPressHtml(post.content?.rendered ?? '')
    },
    featuredImageUrl: featuredImageUrl ?? undefined
  }
}

function getWhatsNewBaseUrl() {
  return isGroupcalApp()
    ? 'https://www.groupcal.app'
    : 'https://blog.twentyfour.me'
}

function createWhatsNewUrl(perPage: number, page = 1) {
  return `${getWhatsNewBaseUrl()}/wp-json/wp/v2/posts?categories=${WHATS_NEW_CATEGORY_IDS}&per_page=${perPage}&page=${page}&_fields=${WHATS_NEW_FIELDS}&lang=${getLanguageCode()}`
}

function asPosts(data: unknown): WhatsNewPost[] {
  if (Array.isArray(data)) return data as WhatsNewPost[]
  if (data) return [data as WhatsNewPost]
  return []
}

export async function fetchWhatsNewPosts(perPage: number, page = 1) {
  const response = await fetch(createWhatsNewUrl(perPage, page))

  if (!response.ok) {
    throw new Error(`Whats new request failed: ${response.status}`)
  }

  return asPosts(await response.json()).map(normalizePost)
}

export async function prefetchLatestWhatsNewPost() {
  const posts = await fetchWhatsNewPosts(1)
  return posts[0] ?? null
}

export function getLastSeenWhatsNewPostId() {
  const raw = LocalStorage.get(LocalStorageKey.LAST_SEEN_POST_ID)
  const parsed = Number(raw ?? 0)
  return Number.isFinite(parsed) ? parsed : 0
}

export function hasUnreadWhatsNew(latestPostId?: number | null) {
  if (!latestPostId) return false
  return latestPostId > getLastSeenWhatsNewPostId()
}

export function markWhatsNewAsSeen(latestPostId?: number | null) {
  if (!latestPostId) return
  LocalStorage.set(LocalStorageKey.LAST_SEEN_POST_ID, String(latestPostId))
}
